import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle2, ArrowRight, Sparkles, Ticket } from 'lucide-react';
import { useEffect } from 'react';
import { useAuth } from '@/lib/auth';
import { Logo } from '@/components/Logo';

export default function StripeSuccess() {
  const [params] = useSearchParams();
  const { user, refreshUser } = useAuth();
  const sessionId = params.get('session_id');
  const type = params.get('type'); // 'billet' | 'saas' | null
  const qr_code = params.get('qr_code');

  useEffect(() => {
    if (user) refreshUser();
    // eslint-disable-next-line
  }, []);

  const isBillet = type === 'billet' && qr_code;

  return (
    <div className="min-h-screen bg-white grid place-items-center p-4" data-testid="stripe-success-page">
      <div className="max-w-lg w-full text-center slide-up">
        <Link to="/" className="inline-flex items-center gap-2 mb-10">
          <Logo size={28} />
          <span className="font-display text-xl font-black tracking-tight text-slate-900">AssoPerf</span>
        </Link>

        <div className="relative inline-block mb-8">
          <div className="w-24 h-24 rounded-full grid place-items-center" style={{ background: 'rgba(16, 185, 129, 0.1)' }}>
            <CheckCircle2 className="w-14 h-14 text-emerald-500" />
          </div>
          <div className="absolute -top-1 -right-1 animate-pulse">
            <Sparkles className="w-6 h-6 text-amber-400" />
          </div>
        </div>

        {isBillet ? (
          <>
            <h1 className="font-display text-4xl lg:text-5xl font-black tracking-tighter text-slate-900 mb-4" data-testid="success-title">
              Paiement confirmé.
            </h1>
            <p className="text-lg text-slate-600 mb-10 max-w-md mx-auto">
              Votre billet est prêt. Présentez le QR code à l'entrée — ou enregistrez-le sur votre téléphone.
            </p>

            <div className="card text-left mb-8">
              <h2 className="font-display text-base font-bold text-slate-900 mb-3">Prochaines étapes</h2>
              <ul className="space-y-2.5">
                {[
                  'Ouvrez votre billet pour voir le QR code',
                  'Ajoutez-le à votre téléphone (capture d\'écran ou lien)',
                  'Partagez l\'événement avec vos amis',
                  'Présentez-le à l\'entrée — c\'est tout',
                ].map((step, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-slate-700">
                    <div className="w-5 h-5 rounded-full grid place-items-center text-[10px] font-bold text-white shrink-0 mt-0.5" style={{ background: '#0052FF' }}>{i + 1}</div>
                    {step}
                  </li>
                ))}
              </ul>
            </div>

            <Link to={`/billet/${qr_code}`} className="btn-primary inline-flex items-center gap-2 !px-7 !py-3.5 !text-base" style={{ background: '#0052FF' }} data-testid="success-billet-cta">
              <Ticket className="w-4 h-4" /> Voir mon billet (QR) <ArrowRight className="w-4 h-4" />
            </Link>
          </>
        ) : (
          <>
            <h1 className="font-display text-4xl lg:text-5xl font-black tracking-tighter text-slate-900 mb-4" data-testid="success-title">
              Bienvenue dans la grande aventure.
            </h1>
            <p className="text-lg text-slate-600 mb-10 max-w-md mx-auto">
              Votre abonnement est activé. Votre association passe en mode pro dès maintenant.
            </p>

            <div className="card text-left mb-8">
              <h2 className="font-display text-base font-bold text-slate-900 mb-3">Vos premières étapes</h2>
              <ul className="space-y-2.5">
                {[
                  'Personnalisez votre couleur et votre logo',
                  'Importez ou ajoutez vos premiers membres',
                  'Créez votre premier événement',
                  'Configurez votre Stripe Connect pour encaisser',
                ].map((step, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-slate-700">
                    <div className="w-5 h-5 rounded-full grid place-items-center text-[10px] font-bold text-white shrink-0 mt-0.5" style={{ background: '#0052FF' }}>{i + 1}</div>
                    {step}
                  </li>
                ))}
              </ul>
            </div>

            <Link to="/dashboard" className="btn-primary inline-flex items-center gap-2 !px-7 !py-3.5 !text-base" style={{ background: '#0052FF' }} data-testid="success-cta">
              Accéder à mon espace <ArrowRight className="w-4 h-4" />
            </Link>
          </>
        )}

        {sessionId && (
          <div className="mt-8 text-[10px] text-slate-400 font-mono">
            Réf. paiement : {sessionId.slice(0, 20)}…
          </div>
        )}
      </div>
    </div>
  );
}
